import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, User, ShoppingBag, Plane } from "lucide-react";
import { NAV, ASSETS } from "@/data/site";
import { useAuth } from "@/context/AuthContext";

export function Nav() {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setHidden(y > 120 && y > last);
      last = y;
      setOpen(false);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-cream border-b border-olive/10 transition-transform duration-300 ${hidden ? "-translate-y-full" : "translate-y-0"}`}
      data-testid="site-nav">
      <div className="mx-auto max-w-[1500px] px-4 md:px-8 h-[74px] flex items-center justify-between gap-4">
        {/* logo lockup */}
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
          <img src={ASSETS.logo} alt="Helserebellen" className="h-12 w-12 object-contain mix-blend-multiply" />
          <div className="leading-none">
            <span className="block font-sans font-extrabold tracking-tight text-[19px] text-olive-deep uppercase">Helserebellen</span>
            <span className="block text-[8px] font-semibold tracking-[0.18em] text-terra uppercase mt-1">Kunnskap. Frihet. Vitalitet.</span>
          </div>
        </Link>

        {/* menu */}
        <nav className="hidden xl:flex items-center gap-6">
          {NAV.map((n, i) => (
            n.external ? (
              <a key={n.label} href={n.href} target="_blank" rel="noopener noreferrer" data-testid={`nav-${i}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-terra px-4 py-1.5 text-[12px] font-semibold uppercase tracking-[0.06em] text-cream hover:bg-olive-deep transition-colors">
                <Plane size={15} strokeWidth={1.8} /> {n.label}
              </a>
            ) : n.href.startsWith("/") ? (
              <Link key={n.label} to={n.href} data-testid={`nav-${i}`}
                className="text-[12px] font-semibold uppercase tracking-[0.06em] transition-colors relative pb-1 text-olive-deep hover:text-terra">
                {n.label}
              </Link>
            ) : (
              <a key={n.label} href={n.href} data-testid={`nav-${i}`}
                className={`text-[12px] font-semibold uppercase tracking-[0.06em] transition-colors relative pb-1 ${
                  i === 0 ? "text-terra after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-full after:bg-terra" : "text-olive-deep hover:text-terra"
                }`}>
                {n.label}
              </a>
            )
          ))}
        </nav>

        {/* icons */}
        <div className="flex items-center gap-4 shrink-0">
          <button onClick={() => navigate(user ? "/dashboard" : "/login")} data-testid="nav-user" className="text-olive-deep hover:text-terra transition-colors">
            <User size={22} strokeWidth={1.6} />
          </button>
          <button onClick={() => navigate("/login")} data-testid="nav-cart" className="text-olive-deep hover:text-terra transition-colors">
            <ShoppingBag size={22} strokeWidth={1.6} />
          </button>
          <button className="xl:hidden text-olive-deep" onClick={() => setOpen(true)} data-testid="nav-mobile-open">
            <Menu size={24} />
          </button>
        </div>
      </div>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ backgroundColor: "#3E4530" }}
              className="fixed inset-0 z-[60] text-cream flex flex-col p-8 xl:hidden">
              <div className="flex justify-between items-center">
                <span className="font-sans font-extrabold uppercase text-2xl">Helserebellen</span>
                <button onClick={() => setOpen(false)} data-testid="nav-mobile-close"><X size={28} /></button>
              </div>
              <div className="flex flex-col gap-5 mt-14">
                {NAV.map((n, i) => (
                  <motion.a key={n.label} href={n.href}
                    target={n.external ? "_blank" : undefined} rel={n.external ? "noopener noreferrer" : undefined}
                    onClick={(e) => {
                      setOpen(false);
                      if (!n.external && n.href.startsWith("/")) { e.preventDefault(); navigate(n.href); }
                    }}
                    initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.08 + i * 0.06 }}
                    className={`font-serif text-3xl transition-colors inline-flex items-center gap-3 ${n.external ? "text-terra-soft" : "text-cream/90 hover:text-terra-soft"}`}>
                    {n.external && <Plane size={26} strokeWidth={1.6} />}{n.label}
                  </motion.a>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
}
